import * as THREE from "three";
import type { AnimationState } from "@mucki/shared";

/**
 * A cat built from primitives (KTD4). The animation state enum is the same one
 * the server synchronizes, so a later rigged glTF can drive an AnimationMixer
 * from these names without redefining them.
 */
export interface CatMesh {
  group: THREE.Group;
  setAnimationState(state: AnimationState): void;
  getAnimationState(): AnimationState;
  update(delta: number): void;
  dispose(): void;
}

const HURT_COLOR = new THREE.Color(0xff5a4a);

/** Stable per-player fur colour so cats are told apart without reading names. */
export function catColorFor(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  return new THREE.Color().setHSL((Math.abs(hash) % 360) / 360, 0.55, 0.6).getHex();
}

export function createCatMesh(color = 0xd9a441): CatMesh {
  const group = new THREE.Group();
  const fur = new THREE.MeshStandardMaterial({ color, roughness: 0.85 });
  const baseColor = new THREE.Color(color);
  const dark = new THREE.MeshStandardMaterial({ color: 0x2b2118, roughness: 0.7 });

  const mesh = (geometry: THREE.BufferGeometry, material: THREE.Material) => {
    const item = new THREE.Mesh(geometry, material);
    item.castShadow = true;
    return item;
  };

  const body = mesh(new THREE.CapsuleGeometry(0.34, 0.7, 4, 10), fur);
  body.rotation.x = Math.PI / 2;
  body.position.y = 0.62;
  group.add(body);

  const head = mesh(new THREE.SphereGeometry(0.3, 14, 12), fur);
  head.position.set(0, 0.85, 0.62);
  group.add(head);

  const snout = mesh(new THREE.ConeGeometry(0.14, 0.22, 8), fur);
  snout.rotation.x = Math.PI / 2;
  snout.position.set(0, -0.05, 0.26);
  head.add(snout);

  for (const side of [-1, 1]) {
    const ear = mesh(new THREE.ConeGeometry(0.12, 0.26, 5), fur);
    ear.position.set(side * 0.16, 0.26, -0.02);
    head.add(ear);

    const eye = mesh(new THREE.SphereGeometry(0.05, 8, 8), dark);
    eye.position.set(side * 0.13, 0.06, 0.24);
    head.add(eye);
  }

  const tailPivot = new THREE.Group();
  tailPivot.position.set(0, 0.72, -0.5);
  const tail = mesh(new THREE.CylinderGeometry(0.06, 0.09, 0.8, 6), fur);
  tail.position.y = 0.4;
  tailPivot.add(tail);
  group.add(tailPivot);

  const legs: THREE.Mesh[] = [];
  for (const x of [-0.2, 0.2]) {
    for (const z of [-0.28, 0.32]) {
      const leg = mesh(new THREE.CylinderGeometry(0.08, 0.07, 0.5, 6), fur);
      leg.position.set(x, 0.25, z);
      group.add(leg);
      legs.push(leg);
    }
  }

  let state: AnimationState = "idle";
  let clock = 0;
  let stateClock = 0;

  const resetPose = () => {
    group.rotation.z = 0;
    body.position.y = 0.62;
    body.rotation.set(Math.PI / 2, 0, 0);
    head.rotation.set(0, 0, 0);
    tailPivot.rotation.set(0, 0, 0);
    legs.forEach((leg, index) => {
      leg.rotation.set(0, 0, 0);
      leg.position.y = 0.25;
      leg.position.z = index % 2 === 0 ? -0.28 : 0.32;
    });
    fur.color.copy(baseColor);
    fur.emissive.setHex(0x000000);
  };

  return {
    group,
    getAnimationState: () => state,
    setAnimationState: (next) => {
      if (next === state) return;
      state = next;
      stateClock = 0;
      resetPose();
    },
    update: (delta) => {
      clock += delta;
      stateClock += delta;

      switch (state) {
        case "moving": {
          const swing = Math.sin(clock * 12) * 0.5;
          legs.forEach((leg, index) => {
            leg.rotation.x = index % 2 === 0 ? swing : -swing;
          });
          body.position.y = 0.62 + Math.abs(Math.sin(clock * 12)) * 0.05;
          tailPivot.rotation.x = -0.4 + Math.sin(clock * 8) * 0.15;
          break;
        }
        case "jumping": {
          legs.forEach((leg) => {
            leg.rotation.x = -0.7;
          });
          tailPivot.rotation.x = -0.9;
          break;
        }
        case "attacking": {
          const lunge = Math.sin(Math.min(stateClock * 12, Math.PI));
          body.rotation.x = Math.PI / 2 - lunge * 0.35;
          head.rotation.x = -lunge * 0.4;
          // Odd indices are the front legs — they come up for the swipe.
          legs.forEach((leg, index) => {
            if (index % 2 === 1) leg.rotation.x = -lunge * 1.1;
          });
          tailPivot.rotation.x = -0.6;
          break;
        }
        case "hurt": {
          const flash = Math.max(0, 1 - stateClock * 3);
          fur.color.copy(baseColor).lerp(HURT_COLOR, flash);
          fur.emissive.setRGB(flash * 0.3, 0, 0);
          group.rotation.z = Math.sin(clock * 30) * 0.06 * flash;
          break;
        }
        case "dead": {
          group.rotation.z = Math.min(stateClock * 4, 1) * (Math.PI / 2);
          tailPivot.rotation.x = 0.2;
          break;
        }
        default: {
          body.position.y = 0.62 + Math.sin(clock * 2) * 0.02;
          tailPivot.rotation.x = -0.2 + Math.sin(clock * 1.6) * 0.2;
          tailPivot.rotation.z = Math.sin(clock * 1.1) * 0.15;
          break;
        }
      }
    },
    dispose: () => {
      group.traverse((object) => {
        if (object instanceof THREE.Mesh) object.geometry.dispose();
      });
      fur.dispose();
      dark.dispose();
      group.removeFromParent();
    },
  };
}
